import type { ReactNode } from 'react';

import styles from './SocialLinks.module.css';

/**
 * Instagram and YouTube are drawn from primitives - legible at 18px and no
 * bundle cost. X is the real wordmark path, because the primitive version read
 * as a close button rather than a logo.
 *
 * `solid` marks a filled icon. A solid glyph carries more optical weight than a
 * 1.8px outline at the same box size, so it renders slightly smaller to sit
 * evenly beside the other two.
 */
interface Icon {
  node: ReactNode;
  solid?: boolean;
}

const ICONS: Record<string, Icon> = {
  Instagram: {
    node: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="5" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="17.2" cy="6.8" r="1.2" fill="currentColor" />
      </>
    ),
  },
  X: {
    solid: true,
    node: (
      <path
        fill="currentColor"
        d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"
      />
    ),
  },
  YouTube: {
    node: (
      <>
        <rect
          x="2"
          y="5"
          width="20"
          height="14"
          rx="4.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <path d="M10.4 9.3 L15.2 12 L10.4 14.7 Z" fill="currentColor" />
      </>
    ),
  },
};

interface SocialLink {
  label: string;
  href: string;
}

const SOCIAL_LINKS: readonly SocialLink[] = [
  { label: 'Instagram', href: 'https://instagram.com/creorank' },
  { label: 'X', href: 'https://x.com/creorank' },
  { label: 'YouTube', href: 'https://youtube.com/@creorank' },
];

/**
 * An alternative to signing up, for people who would rather follow than hand
 * over an email address. Sits directly below the waitlist card so it reads as
 * the second option, not as footer boilerplate.
 */
export function SocialLinks() {
  return (
    <div className={styles.wrap}>
      <p className={styles.label}>You can also follow us on</p>
      <div className={styles.links}>
        {SOCIAL_LINKS.map(({ label, href }) => (
          <a
            key={label}
            className={styles.link}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            title={label}
          >
            <svg
              viewBox="0 0 24 24"
              className={ICONS[label]?.solid === true ? styles.solid : undefined}
              aria-hidden="true"
              focusable="false"
            >
              {ICONS[label]?.node}
            </svg>
          </a>
        ))}
      </div>
    </div>
  );
}
